import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'
import SearchIcon from '@mui/icons-material/Search'
import * as XLSX from 'xlsx'
import { clientStoreService, riderService } from '../services'
import type { RiderAttendanceAnalyticsRow } from '../services/riderService'
import vehicleService from '../services/vehicleService'
import { hasAnyRole } from '../utils/roleUtils';

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-IN')
}

const displayValue = (value?: string | number | null) => value === null || value === undefined || value === '' ? '-' : String(value)
const formatAnalyticsDate = (value: string) => value.replace(/-/g, '/')
const toSortableAnalyticsDate = (value: string) => {
  const [day, month, year] = value.split('-')
  return `${year}-${month}-${day}`
}
const getLatestFirstOrderCounts = (orderCounts: RiderAttendanceAnalyticsRow['orderCounts']) =>
  [...orderCounts]
    .sort((left, right) => toSortableAnalyticsDate(right.date).localeCompare(toSortableAnalyticsDate(left.date)))
    .slice(0, 15)
const getEffIneffFromAverage = (average: number) => {
  if (average >= 19) return 'High frequency'
  if (average >= 13) return 'Mid frequency'
  if (average >= 1) return 'Low frequency'
  return '0 Orders'
}
const getEffIneffColor = (value: string) => {
  if (value === 'High frequency') return 'green'
  if (value === 'Mid frequency') return '#b58900'
  if (value === 'Low frequency') return 'orange'
  return 'red'
}
const canExport = hasAnyRole([
  'Super Admin',
  'MIS_Management',
]);
const analyticsColumnWidths = [190, 170, 150, 180, 150, 140, 220, 130, 120, 150, 170, 100, 170, 160, 130]
const analyticsDateColumnWidth = 130
const stickyColumnLeftOffsets = [0, 190, 360, 510, 690, 840]
const getStickyColumnSx = (index: number, backgroundColor: string) =>
  index < stickyColumnLeftOffsets.length
    ? {
        position: 'sticky' as const,
        left: stickyColumnLeftOffsets[index],
        width: analyticsColumnWidths[index],
        minWidth: analyticsColumnWidths[index],
        maxWidth: analyticsColumnWidths[index],
        boxSizing: 'border-box' as const,
        zIndex: index === stickyColumnLeftOffsets.length - 1 ? 3 : 2,
        backgroundColor,
        ...(index === stickyColumnLeftOffsets.length - 1
          ? { boxShadow: '4px 0 6px -4px rgba(0, 0, 0, 0.45)' }
          : {}),
      }
    : {}

const RiderAttendanceAnalytics: React.FC = () => {
  const [rows, setRows] = useState<RiderAttendanceAnalyticsRow[]>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [total, setTotal] = useState(0)
  const [dateColumns, setDateColumns] = useState<RiderAttendanceAnalyticsRow['orderCounts']>([])
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [city, setCity] = useState('')
  const [clientNamesSelected, setClientNamesSelected] = useState<string[]>([])
  const [cities, setCities] = useState<string[]>([])
  const [clientNames, setClientNames] = useState<string[]>([])
  const [loadingCities, setLoadingCities] = useState(false)
  const [loadingClients, setLoadingClients] = useState(false)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const selectedClientNames = clientNamesSelected.join(',')

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await riderService.getRiderAttendanceAnalytics({
        page: page + 1,
        limit: rowsPerPage,
        search,
        city,
        clientName: selectedClientNames,
      })

      if (!response.success) {
        throw new Error(response.message || 'Failed to load rider attendance analytics')
      }

      const apiRows = Array.isArray(response.data) ? response.data : []
      setRows(apiRows)
      setDateColumns(getLatestFirstOrderCounts(apiRows[0]?.orderCounts || []))
      setTotal(response.pagination?.totalItems || 0)
    } catch (err: any) {
      setRows([])
      setDateColumns([])
      setTotal(0)
      setError(err.message || 'Failed to load rider attendance analytics')
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, search, city, clientNamesSelected])

  const handleExport = useCallback(async () => {
    try {
      setExporting(true)
      setError(null)

      const firstResponse = await riderService.getRiderAttendanceAnalytics({
        page: 1,
        limit: 20,
        search,
        city,
        clientName: selectedClientNames,
      })

      if (!firstResponse.success) {
        throw new Error(firstResponse.message || 'Failed to export rider attendance analytics')
      }

      const allRows = [...(firstResponse.data || [])]
      const totalPages = firstResponse.pagination?.totalPages || 1

      for (let exportPage = 2; exportPage <= totalPages; exportPage += 1) {
        const response = await riderService.getRiderAttendanceAnalytics({
          page: exportPage,
          limit: 20,
          search,
          city,
          clientName: selectedClientNames,
        })

        if (!response.success) {
          throw new Error(response.message || 'Failed to export rider attendance analytics')
        }

        allRows.push(...(response.data || []))
      }

      if (allRows.length === 0) {
        throw new Error('No rider attendance data found for the selected filters')
      }

      const exportDateColumns = getLatestFirstOrderCounts(allRows[0].orderCounts || [])
      const exportRows = allRows.map((row) => {
        const exportRow: Record<string, string | number> = {
          'Rider ID': displayValue(row.publicRiderId || row.riderId),
          'Rider Name': displayValue(row.name),
          'Rider Mob No': displayValue(row.phone),
          Client: displayValue(row.clientName),
          clientRiderId: displayValue(row.clientRiderId),
          City: displayValue(row.city),
          Source: displayValue(row.referredById),
          DOB: displayValue(row.dob),
          LWD: displayValue(row.lastWorkingDay),
          'Vehicle Assigned At': formatDate(row.vehicleAssignedAt),
          'Vehicle Registration': displayValue(row.vehicleRegistrationNumber),
          WSD: displayValue(row.needsEvRental),
          Status: displayValue(row.status),
          'Eff/Ineff': getEffIneffFromAverage(row.average),
          Average: row.average,
        }

        exportDateColumns.forEach(({ date }) => {
          exportRow[formatAnalyticsDate(date)] = row.orderCounts.find((item) => item.date === date)?.orderCount ?? 0
        })

        return exportRow
      })

      const worksheet = XLSX.utils.json_to_sheet(exportRows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance Analytics')
      XLSX.writeFile(workbook, `rider-attendance-analytics-${new Date().toISOString().slice(0, 10)}.xlsx`)
    } catch (err: any) {
      setError(err.message || 'Failed to export rider attendance analytics')
    } finally {
      setExporting(false)
    }
  }, [search, city, clientNamesSelected])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(0)
      setSearch(searchInput.trim())
    }, 400)

    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    const loadCities = async () => {
      try {
        setLoadingCities(true)
        const response = await vehicleService.getCities()
        const cityNames: string[] = Array.isArray(response?.data)
          ? response.data
              .map((item: any) => String(item?.name || '').trim())
              .filter(Boolean)
          : []
        setCities(
          cityNames
            .filter((name, index) => cityNames.indexOf(name) === index)
            .sort((left, right) => left.localeCompare(right)),
        )
      } catch {
        setCities([])
      } finally {
        setLoadingCities(false)
      }
    }

    loadCities()
  }, [])

  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoadingClients(true)
        const response = await clientStoreService.getClients({ page: 1, limit: 1000 })
        const names = Array.isArray(response?.data)
          ? response.data
              .map((client) => String(client?.name || '').trim())
              .filter(Boolean)
          : []
        setClientNames([...new Set(names)].sort((left, right) => left.localeCompare(right)))
      } catch {
        setClientNames([])
      } finally {
        setLoadingClients(false)
      }
    }

    loadClients()
  }, [])

  useEffect(() => {
    loadAnalytics()
  }, [loadAnalytics])

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Rider Attendance Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Latest 15 days of rider order activity</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={loading || exporting || !canExport}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
          <IconButton onClick={loadAnalytics} disabled={loading || exporting} aria-label="Refresh attendance analytics" title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'minmax(280px, 2fr) repeat(4, minmax(150px, 1fr))',
          },
          gap: 1.5,
          mb: 2,
        }}
      >
        <TextField
          size="small"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Search by Mobile Number, Rider ID, Public Rider ID..."
          sx={{ width: '100%', minWidth: 0, gridColumn: { xs: 'auto', sm: '1 / -1', lg: 'auto' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Autocomplete
          size="small"
          options={cities}
          value={city || null}
          onChange={(_, selectedCity) => {
            setCity(selectedCity || '')
            setPage(0)
          }}
          loading={loadingCities}
          sx={{ width: '100%', minWidth: 0 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="City"
              placeholder="Select city"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingCities ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <FormControl size="small" sx={{ width: '100%', minWidth: 0 }}>
          <InputLabel id="client-name-filter-label" shrink>Client Name</InputLabel>
          <Select
            labelId="client-name-filter-label"
            multiple
            value={clientNamesSelected}
            label="Client Name"
            onChange={(event) => {
              const selected = typeof event.target.value === 'string'
                ? event.target.value.split(',').filter(Boolean)
                : event.target.value

              setClientNamesSelected(selected.filter(Boolean))
              setPage(0)
            }}
            displayEmpty
            disabled={loadingClients}
            renderValue={(selected) => selected.length ? selected.join(', ') : 'All client names'}
          >
            <MenuItem value="" onClick={() => {
              setClientNamesSelected([])
              setPage(0)
            }}>
              <Checkbox checked={clientNamesSelected.length === 0} />
              <ListItemText primary="All client names" />
            </MenuItem>
            {clientNames.map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox checked={clientNamesSelected.includes(name)} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table
              size="small"
              sx={{
                minWidth: 3440,
                tableLayout: 'fixed',
                '& .MuiTableBody-root .MuiTableCell-root': {
                  py: 0.25,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  verticalAlign: 'middle',
                },
              }}
            >
              <colgroup>
                {[...analyticsColumnWidths, ...dateColumns.map(() => analyticsDateColumnWidth)].map((width, index) => (
                  <col key={`analytics-column-${index}`} style={{ width }} />
                ))}
              </colgroup>
              <TableHead sx={{ backgroundColor: '#dbeafe' }}>
                <TableRow>
                  {['Rider ID', 'Rider Name', 'Rider Mob No', 'Client', 'clientRiderId', 'City', 'Source', 'DOB', 'LWD', 'Vehicle Assigned At', 'Vehicle Registration', 'WSD', 'Status', 'Eff/Ineff', 'Average', ...dateColumns.map(({ date }) => formatAnalyticsDate(date))].map((heading, index) => (
                    <TableCell
                      key={heading}
                      align={index >= 13 ? 'center' : 'left'}
                      sx={{
                        width: analyticsColumnWidths[index] || analyticsDateColumnWidth,
                        minWidth: analyticsColumnWidths[index] || analyticsDateColumnWidth,
                        maxWidth: analyticsColumnWidths[index] || analyticsDateColumnWidth,
                        whiteSpace: 'nowrap',
                        overflow: index >= analyticsColumnWidths.length ? 'visible' : 'hidden',
                        textOverflow: index >= analyticsColumnWidths.length ? 'clip' : 'ellipsis',
                        fontWeight: 800,
                        ...getStickyColumnSx(index, '#dbeafe'),
                        zIndex: index < stickyColumnLeftOffsets.length ? 4 : 1,
                      }}
                    >
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={30} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={30} align="center" sx={{ py: 5 }}>No rider attendance data found</TableCell></TableRow>
                ) : rows.map((row) => {
                  return (
                    <TableRow key={row.riderId} hover sx={{ height: 28 }}>
                      <TableCell sx={{
                        width: analyticsColumnWidths[0],
                        overflow: 'hidden',
                        textOverflow: 'clip',
                        ...getStickyColumnSx(0, 'background.paper'),
                      }}>
                        {displayValue(row.publicRiderId || row.riderId)}
                      </TableCell>
                      <TableCell sx={{ width: analyticsColumnWidths[1], ...getStickyColumnSx(1, 'background.paper') }}>{displayValue(row.name)}</TableCell>
                      <TableCell sx={{ width: analyticsColumnWidths[2], ...getStickyColumnSx(2, 'background.paper') }}>{displayValue(row.phone)}</TableCell>
                      <TableCell sx={getStickyColumnSx(3, 'background.paper')}>{displayValue(row.clientName)}</TableCell>
                      <TableCell sx={getStickyColumnSx(4, 'background.paper')}>{displayValue(row.clientRiderId)}</TableCell>
                      <TableCell sx={getStickyColumnSx(5, 'background.paper')}>{displayValue(row.city)}</TableCell>
                      <TableCell>{displayValue(row.referredById)}</TableCell>
                      <TableCell>{displayValue(row.dob)}</TableCell>
                      <TableCell>{displayValue(row.lastWorkingDay)}</TableCell>
                      <TableCell>{formatDate(row.vehicleAssignedAt)}</TableCell>
                      <TableCell>{displayValue(row.vehicleRegistrationNumber)}</TableCell>
                      <TableCell>{displayValue(row.needsEvRental)}</TableCell>
                      <TableCell>{displayValue(row.status)}</TableCell>
                      <TableCell
                        sx={{
                          color: getEffIneffColor(getEffIneffFromAverage(row.average)),
                          fontWeight: 700,
                        }}
                      >
                        {getEffIneffFromAverage(row.average)}
                      </TableCell>
                      <TableCell align="center">
                        {row.average}
                      </TableCell>
                      {dateColumns.map(({ date }) => {
                        const countForDate = row.orderCounts.find((item) => item.date === date)?.orderCount
                        return (
                          <TableCell key={`${row.riderId}-${date}`} align="center">
                            {countForDate ?? 0}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 20]}
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value))
              setPage(0)
            }}
          />
        </CardContent>
      </Card>
    </Box>
  )
}

export default RiderAttendanceAnalytics
